import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Button,
  CheckIcon,
  cn,
  Input,
  Label,
  PlusIcon,
  PopoverContent,
} from '@playerbase/ui';

import { useAuth } from '../../auth/use-auth.js';
import {
  createBuildMarker,
  updateBuildMarker,
} from '../../games/build-markers-data.js';
import { STAGE_BADGE_CLASSES } from '../../games/stage-colour-classes.js';
import { useShell } from '../use-shell.js';

type BuildPillDropdownProps = {
  onClose: () => void;
};

const ONE_DAY_MS = 24 * 60 * 60 * 1000;

function relativeTime(timestamp: string | null): string {
  if (timestamp === null) return 'recently';
  const date = new Date(timestamp);
  const diff = Date.now() - date.getTime();
  if (diff < 60_000) return 'just now';
  if (diff < ONE_DAY_MS) {
    const minutes = Math.floor(diff / 60_000);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    return `${hours}h ago`;
  }
  const days = Math.floor(diff / ONE_DAY_MS);
  if (days < 30) return `${days}d ago`;
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

export function BuildPillDropdown({ onClose }: BuildPillDropdownProps): JSX.Element | null {
  const {
    activeStudio,
    activeGame,
    activeBuildMarker,
    activeStage,
    devStages,
    refreshBuilds,
  } = useShell();
  const { user } = useAuth();

  const [showNewForm, setShowNewForm] = useState(false);
  const [newLabel, setNewLabel] = useState('');
  const [busy, setBusy] = useState(false);

  // Reset the inline form whenever the popover closes between opens.
  useEffect(() => {
    if (!showNewForm) setNewLabel('');
  }, [showNewForm]);

  const buildsLink =
    activeGame !== null
      ? `/s/${activeStudio.shortId}/g/${activeGame.shortId}/game-settings/builds`
      : null;

  const handlePickStage = useCallback(
    async (stageId: string): Promise<void> => {
      if (activeBuildMarker === null) return;
      if (stageId === activeBuildMarker.stageId) {
        onClose();
        return;
      }
      setBusy(true);
      try {
        await updateBuildMarker(activeBuildMarker.id, { stageId });
        await refreshBuilds();
      } finally {
        setBusy(false);
        onClose();
      }
    },
    [activeBuildMarker, refreshBuilds, onClose],
  );

  const handleCreate = useCallback(async (): Promise<void> => {
    if (activeGame === null) return;
    const trimmed = newLabel.trim();
    if (trimmed.length === 0) return;
    const firstStage = devStages[0];
    if (firstStage === undefined) return;

    setBusy(true);
    try {
      await createBuildMarker({
        gameId: activeGame.id,
        label: trimmed,
        stageId: firstStage.id,
        createdByUserId: user?.id ?? null,
      });
      await refreshBuilds();
    } finally {
      setBusy(false);
      setShowNewForm(false);
      setNewLabel('');
      onClose();
    }
  }, [activeGame, newLabel, devStages, user, refreshBuilds, onClose]);

  if (activeGame === null) return null;

  return (
    <PopoverContent className="w-[20rem] p-0" align="start">
      {/* Header */}
      <div className="flex items-center gap-2 border-b border-border px-3 py-2 text-xs">
        {activeBuildMarker === null ? (
          <span className="text-text-muted">No build tagged yet</span>
        ) : (
          <>
            <span className="font-mono text-text">{activeBuildMarker.label}</span>
            {activeStage !== null && (
              <span
                className={cn(
                  'rounded-sm border px-1.5 py-0.5 text-[10px] font-mono uppercase tracking-wider',
                  STAGE_BADGE_CLASSES[activeStage.colour],
                )}
              >
                {activeStage.label}
              </span>
            )}
          </>
        )}
      </div>

      {/* Stage selector — only meaningful when a build exists */}
      {activeBuildMarker !== null && devStages.length > 0 && (
        <>
          <div className="px-3 pt-2 pb-1 text-[10px] font-mono uppercase tracking-wider text-text-muted">
            Development stage
          </div>
          <div className="no-scrollbar max-h-[14rem] overflow-y-auto">
            {devStages.map((stage) => (
              <button
                key={stage.id}
                type="button"
                disabled={busy}
                onClick={() => void handlePickStage(stage.id)}
                className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-text hover:bg-surface-2 disabled:opacity-60"
              >
                <span
                  className={cn(
                    'rounded-sm border px-1.5 py-0.5 text-[10px] font-mono uppercase tracking-wider',
                    STAGE_BADGE_CLASSES[stage.colour],
                  )}
                >
                  {stage.label}
                </span>
                <span className="flex-1" />
                {stage.id === activeBuildMarker.stageId && (
                  <CheckIcon className="text-accent" />
                )}
              </button>
            ))}
          </div>
        </>
      )}

      {/* Freshness — manual mode only for now. Auto-update integrations
          slot in here later with a "Last fetched from {Source}" variant. */}
      {activeBuildMarker !== null && (
        <div className="border-t border-border px-3 py-2 text-[11px] text-text-muted">
          Created {relativeTime(activeBuildMarker.createdAt)}
        </div>
      )}

      {/* New-build inline form */}
      <div className="border-t border-border p-2">
        {showNewForm ? (
          <div className="space-y-2">
            <div className="flex flex-col gap-1">
              <Label htmlFor="quick-build-label" className="text-[10px] uppercase tracking-wider text-text-muted">
                Build label
              </Label>
              <Input
                id="quick-build-label"
                autoFocus
                value={newLabel}
                onChange={(e) => setNewLabel(e.target.value)}
                placeholder="v0.4.3"
                disabled={busy}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    void handleCreate();
                  } else if (e.key === 'Escape') {
                    setShowNewForm(false);
                  }
                }}
              />
            </div>
            <div className="flex items-center justify-end gap-2">
              <Button
                size="sm"
                variant="ghost"
                disabled={busy}
                onClick={() => setShowNewForm(false)}
              >
                Cancel
              </Button>
              <Button
                size="sm"
                variant="primary"
                disabled={busy || newLabel.trim().length === 0}
                onClick={() => void handleCreate()}
              >
                {busy ? 'Saving…' : 'Log build'}
              </Button>
            </div>
            {devStages.length > 0 && (
              <p className="text-[11px] text-text-muted">
                Defaults to the first stage in your config ({devStages[0]?.label}). Use
                the full form on the Builds page for explicit choice.
              </p>
            )}
          </div>
        ) : (
          <Button
            size="sm"
            variant="ghost"
            disabled={devStages.length === 0}
            onClick={() => setShowNewForm(true)}
            className="w-full justify-start"
          >
            <PlusIcon />
            <span>New build</span>
          </Button>
        )}
      </div>

      {/* View all builds */}
      {buildsLink !== null && (
        <div className="border-t border-border">
          <Link
            to={buildsLink}
            onClick={onClose}
            className="block px-3 py-2 text-sm text-text-muted hover:bg-surface-2 hover:text-text"
          >
            View all builds →
          </Link>
        </div>
      )}
    </PopoverContent>
  );
}
